/**
 * @file DataLineagePage.tsx
 * @description US4 — Data Lineage (Datasources) page with KPI cards, filters, table, CSV export
 * @feature 142-governance-compliance-dashboard
 * @constitution v1.12.0
 * Conformed to Spec 197 design blueprint
 */

import React, { useState, useMemo } from 'react';
import {
  Database as DatabaseIcon,
  Layers,
  Router,
  Search,
  Download,
  GitBranch,
} from 'lucide-react';
import { useDatasources, downloadCsv } from '../../../services/operations/governanceService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  Badge,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  Extended,
  QuickGlance,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import type { DatasourceEntry } from '../../../types/governance';

// ============================================================================
// Sub-components
// ============================================================================

type SortDir = 'asc' | 'desc';

// ============================================================================
// Main Component
// ============================================================================

const DataLineagePage: React.FC = () => {
  const { data, isLoading, error, refetch } = useDatasources();

  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [workspaceFilter, setWorkspaceFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField] = useState<string>('semanticModelName');
  const [sortDir] = useState<SortDir>('asc');

  const dsTypes = useMemo(() => {
    if (!data?.datasources) return [];
    return [...new Set(data.datasources.map((d) => d.datasourceType))].sort();
  }, [data?.datasources]);

  const filtered = useMemo(() => {
    if (!data?.datasources) return [];
    let list = data.datasources;

    if (typeFilter !== 'all') list = list.filter((d) => d.datasourceType === typeFilter);
    if (workspaceFilter) {
      const lower = workspaceFilter.toLowerCase();
      list = list.filter((d) => d.workspaceName.toLowerCase().includes(lower));
    }
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(
        (d) =>
          d.semanticModelName.toLowerCase().includes(lower) ||
          d.datasourceType.toLowerCase().includes(lower) ||
          (d.server && d.server.toLowerCase().includes(lower)) ||
          (d.databaseName && d.databaseName.toLowerCase().includes(lower)),
      );
    }

    return [...list].sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortField] ?? '';
      const bVal = (b as unknown as Record<string, unknown>)[sortField] ?? '';
      const cmp = String(aVal).localeCompare(String(bVal));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data?.datasources, typeFilter, workspaceFilter, searchTerm, sortField, sortDir]);

  const handleExportCsv = () => {
    const date = new Date().toISOString().split('T')[0];
    let csv = 'Semantic Model,Workspace,Datasource Type,Server,Database,Gateway\n';
    filtered.forEach((d) => {
      csv += `"${d.semanticModelName}","${d.workspaceName}","${d.datasourceType}","${d.server || ''}","${d.databaseName || ''}","${d.gatewayName || ''}"\n`;
    });
    downloadCsv(csv, `data-lineage-${date}.csv`);
  };

  const isEmpty = !isLoading && (!data?.datasources || data.datasources.length === 0);
  const summary = data?.summary;

  const muted = <span className="text-[var(--color-text-secondary)]">—</span>;

  const columns: DataTableColumn<DatasourceEntry>[] = [
    {
      key: 'semanticModelName',
      header: 'Semantic Model',
      width: '22%',
      render: (d) => <span className="font-medium text-[var(--color-text-primary)]">{d.semanticModelName}</span>,
    },
    { key: 'workspaceName', header: 'Workspace', width: '18%' },
    {
      key: 'datasourceType',
      header: 'Datasource Type',
      width: '16%',
      render: (d) => (
        <Badge variant="info" isOutlined>
          {d.datasourceType}
        </Badge>
      ),
    },
    {
      key: 'server',
      header: 'Server',
      width: '18%',
      render: (d) => d.server || muted,
    },
    {
      key: 'databaseName',
      header: 'Database',
      width: '14%',
      render: (d) => d.databaseName || muted,
    },
    {
      key: 'gatewayName',
      header: 'Gateway',
      width: '12%',
      render: (d) => d.gatewayName || muted,
    },
  ];

  return (
    <PageLayout
      title="Data Lineage"
      description="Semantic model datasource connections"
      icon={<GitBranch className="h-6 w-6" />}
            helpId="governance.data-lineage"
            onRefresh={() => { void refetch(); }}
            isRefreshing={isLoading}
            actions={
        <>
          <Button leftIcon={<Download className="h-4 w-4" />} onClick={handleExportCsv} disabled={isEmpty}>
            Export CSV
          </Button>
        </>
      }
    >
      {error ? (
        <ErrorState
          title="Failed to load datasources"
          message={error instanceof Error ? error.message : 'Unknown error'}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          {/* KPI tiles */}
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              label="Total Connections"
              value={isEmpty ? '—' : (summary?.totalConnections ?? '—')}
              icon={<Layers className="h-4 w-4" />}
            />
            <StatCard
              label="Datasource Types"
              value={isEmpty ? '—' : (summary?.uniqueDatasourceTypes ?? '—')}
              icon={<DatabaseIcon className="h-4 w-4" />}
            />
            <StatCard
              label="Gateway-Dependent"
              value={isEmpty ? '—' : (summary?.gatewayDependentCount ?? '—')}
              icon={<Router className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the full details.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Datasource type breakdown */}
          {summary?.byDatasourceType && summary.byDatasourceType.length > 0 && (
            <Card className="mb-6 p-4">
              <h3 className="mb-2 text-sm font-medium text-[var(--color-text-primary)]">Datasource Types</h3>
              <div className="flex flex-wrap gap-2">
                {summary.byDatasourceType.map((t) => (
                  <Badge key={t.datasourceType} variant="secondary">
                    {t.datasourceType} ({t.count})
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {isLoading ? (
            <Card padding="none">
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            </Card>
          ) : isEmpty ? (
            <Card>
              <EmptyState
                icon={<DatabaseIcon className="h-10 w-10" />}
                title="No governance data collected yet"
                message="Governance collection requires Fabric Admin API access. Contact your admin to enable the governance collector."
              />
            </Card>
          ) : (
            <>
              {/* Filters */}
              <Card className="mb-6 p-4">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
                  <div className="md:col-span-5">
                    <Input
                      isFullWidth
                      placeholder="Search models, types, servers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      leftAddon={<Search className="h-4 w-4" />}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Select
                      isFullWidth
                      label="Datasource Type"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      options={[{ value: 'all', label: 'All Types' }, ...dsTypes.map((t) => ({ value: t, label: t }))]}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Input
                      isFullWidth
                      label="Workspace"
                      placeholder="Workspace..."
                      value={workspaceFilter}
                      onChange={(e) => setWorkspaceFilter(e.target.value)}
                    />
                  </div>
                  <div className="text-sm text-[var(--color-text-secondary)] md:col-span-2">
                    {filtered.length} of {data?.datasources?.length ?? 0}
                  </div>
                </div>
              </Card>

              {/* Table */}
              <Card padding="none">
                <DataTable
                  columns={columns}
                  rows={filtered}
                  rowKey={(d, idx) => `${d.semanticModelName}-${d.datasourceType}-${idx}`}
                  emptyMessage="No datasources match the current filters"
                  minWidth="52rem"
                />
              </Card>
            </>
          )}
          </Extended>
        </>
      )}
    </PageLayout>
  );
};

export default DataLineagePage;
